import React, { useState } from 'react';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import NoteSelectionModal from '../components/NoteSelectionModal';
import api from '../services/api';
import { FiDownload, FiFileText, FiList, FiZap } from 'react-icons/fi';

const Summarizer = ({ onLogout }) => {
  const [notes, setNotes] = useState('');
  const [summary, setSummary] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const handleSummarize = async () => {
    if (!notes.trim()) {
      setError('Please enter some notes content or select a file from the library.');
      return;
    }

    setLoading(true);
    setError(null);
    setSummary('');

    try {
      const response = await api.post('/summarize', { notes });
      setSummary(response.summary);
    } catch (err) {
      console.error('Summarization failed:', err);
      const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Failed to generate summary. Please try again later.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleNoteSelect = async (note) => {
    setShowModal(false);
    setLoading(true);
    setError(null);
    setSummary('');
    setNotes(`[Selected Note: ${note.title}]\n(Processing document on server...)`);

    try {
      const response = await api.post('/summarize', { noteId: note._id });
      setSummary(response.summary);
    } catch (err) {
      console.error('Summarization failed:', err);
      const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Failed to generate summary. Please try again later.';
      setError(errorMessage);
      setNotes('');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!summary) return;

    const element = document.createElement('a');
    const file = new Blob([summary], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = 'notes_summary.txt';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  return (
    <div className="dashboard-container">
      <Sidebar onLogout={onLogout} />

      <div className="main-content">
        <Header
          title="AI Notes Summarizer"
          subtitle="Turn lengthy study materials and PDF notes into concise key takeaways instantly."
        />

        <div className="card">
          <div className="d-flex justify-between align-center mb-3">
            <label htmlFor="notes-input" style={{ fontSize: '14px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FiFileText /> Source Content
            </label>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => setShowModal(true)}
            >
              <FiList /> Select from Library
            </button>
          </div>

          <div className="form-group mb-3">
            <textarea
              id="notes-input"
              className="form-input"
              style={{
                minHeight: '220px',
                fontFamily: 'inherit',
                lineHeight: '1.6'
              }}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Paste text notes content here or choose a document from your library..."
            />
          </div>

          {error && <div className="alert alert-error mb-3">{error}</div>}

          <button
            className="btn btn-primary"
            onClick={handleSummarize}
            disabled={loading}
          >
            <FiZap /> {loading ? 'Analyzing & Summarizing...' : 'Summarize Notes'}
          </button>
        </div>

        {summary && (
          <div className="card mt-4">
            <div className="d-flex justify-between align-center mb-3 pb-3" style={{ borderBottom: '1px solid var(--border-subtle)' }}>
              <div>
                <h3 style={{ fontSize: '18px', fontWeight: '600' }}>AI Summary Result</h3>
                <span className="text-sm text-secondary">Structured key concepts and takeaways</span>
              </div>
              <button className="btn btn-secondary btn-sm" onClick={handleDownload}>
                <FiDownload /> Download Summary
              </button>
            </div>

            <div
              style={{
                backgroundColor: 'var(--bg-surface-hover)',
                padding: '20px',
                borderRadius: 'var(--radius-md)',
                whiteSpace: 'pre-wrap',
                lineHeight: '1.7',
                fontSize: '14px',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-subtle)'
              }}
            >
              {summary}
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <NoteSelectionModal
          onClose={() => setShowModal(false)}
          onSelect={handleNoteSelect}
        />
      )}
    </div>
  );
};

export default Summarizer;
